/**
 * FileReader
**/
export default class FileReader {
  /**
   * Cosntructor
   * @param {object}
   * {array} ignoreLines that start with ['#', '-']
   * {string} skipFirstColumn avoid the parse of the frist column for ssv
   * {string} type (tsv, json)
   * {string} trimLines number to remove from txt file
   * **/
  constructor({ ignoreLines = null, skipFirstColumn = false, type = 'tsv', trimLines = 0 }) {
    this.options = {
      ignoreLines,
      skipFirstColumn,
      type,
      trimLines,
    };
  }

  /**
   * Read and parse file
   * @param {object}
   * {string} content of the file
   * {string} url of the file
   * {*} options override
   * @return {array} of DecorativeAxis
  **/
  async parse(payload) {
    let { content, ignoreLines, skipFirstColumn, type, trimLines, url } = {
      ...this.options,
      ...payload,
    };
    if (content || url) {
      content = content || (url && await this.read(url, type));
      return this._content({ content, ignoreLines, skipFirstColumn, type, trimLines });
    }
    throw new Error('File reader error: content and url param cannot be empty in parse function call.');
  }

  /**
   * Read and parse dict file
   * @param {object}
   * {string} content
   * @return {string} parsed dict content
  **/
  async dict(content) {
    content = content.split('\n').reduce((chromosomes, line) => {
      if (line) {
        chromosomes.push(line.split('\t').slice(1).reduce((obj, col) => {
          let [field, value] = col.split(':');
          if (field === 'SN') {
            field = 'label';
            value = value.replace('chr', '');
          } else {
            field = 'length';
            value = Number(value);
          }
          return { ...obj, [field]: value };
        }, {}));
      }
      return chromosomes;
    }, []);
    return content;
  }

  /**
   * Wrapper for d3-dsv tsvParse
   * @param {string} text
   * @param {bool} skipFirstColumn
   * @return {object} tsvParse
  **/
  tsv(text, skipFirstColumn) {
    return this.xsv(text, skipFirstColumn, '\t');
  }

  /**
   * SSV parser
   * @param {string} text
   * @param {bool} skipFirstColumn
   * @return {object} ssvParse
  **/
  ssv(text, skipFirstColumn) {
    return this.xsv(text, skipFirstColumn, ' ');
  }

  /**
   * CSV parser
   * @param {string} text
   * @param {bool} skipFirstColumn
   * @return {object} csvParse
  **/
  csv(text, skipFirstColumn) {
    return this.xsv(text, skipFirstColumn);
  }

  /**
   * XSV parser
   * @param {string} text
   * @param {bool} skipFirstColumn
   * @param {string} splitter default ,
   * @return {object} xsvParse
  **/
  xsv(text, skipFirstColumn, splitter = ',') {
    if (typeof text === 'string') {
      const lines = text.split('\n');
      if (lines.length > 0) {
        const cols = skipFirstColumn
          ? ['firstCol', ...lines[0].split(splitter)]
          : lines[0].split(splitter).map((col, index) => {
            col = col.replace(/("|'|\r)/g, '');
            return col || `col${index}`;
          });
        return lines.slice(1, lines.length).map((row) => {
          const rowAsObj = {};
          if (row) {
            row = row.split(splitter);
            cols.forEach((key, col) => {
              if (row[col]) {
                rowAsObj[key] = row[col].replace(/("|')/g, '');
              }
            });
            return rowAsObj;
          }
          return null;
        }).filter((item) => Boolean(item));
      }
    }
    return null;
  }

  /**
   * Get the text content from a url
   * @param {string} url
   * @param {string} type (tsv, json)
  **/
  async read(url, type = 'tsv') {
    try {
      const file = await fetch(url, {
        cache: 'no-cache',
        credentials: 'include',
        method: 'GET',
        mode: 'cors',
      });
      return [400, 403, 404].indexOf(file.status) === -1
        ? type === 'json'
          ? await file.json()
          : await file.text()
        : null;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Remove the lines starting with
   * @param {string} text
   * @param {array} startWith
   * @return {string}
  **/
  removeIgnoreLines(text, startWith) {
    if (typeof text === 'string' && startWith) {
      text = text.replace(new RegExp(`^(${startWith.join('|')}).*(\r\n|\n)`, 'gm'), '');
    }
    return text;
  }

  /**
   * Remove the n firsts lines of a string
   * @param {string} text
   * @param {number} n lines to be removed
   * @return {string}
  **/
  removeFirstLines(text, n = 0) {
    if (typeof text === 'string') {
      for (let i = 0; i < n; i++) {
        text = text.substring(text.indexOf('\n') + 1);
      }
    }
    return text;
  }

  /**
   * Read and parse file
   * @param {object}
   * {*} options override
   * @return {array} of DecorativeAxis
  **/
  async _content(payload) {
    let { content, ignoreLines, skipFirstColumn, type, trimLines } = {
      ...this.options,
      ...payload,
    };
    if (content) {
      content = this.removeIgnoreLines(content, ignoreLines);
      content = this.removeFirstLines(content, trimLines);
      if (this[type]) {
        content = this[type](content, skipFirstColumn);
      }
    }
    return content;
  }
}
